import React, { useState, useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import Header from "../components/Header";
import { hostLogin } from "../store/thunks/hostActions";
import { useNavigate } from "react-router-dom";
import { Link } from "react-router-dom";
import { Alert, CircularProgress } from "@mui/material";
import { useFormik } from "formik";
import { loginSchema } from "../schemas";
import { hostLoginReset } from "../store/slices/hostSlices";
import ReCAPTCHA from "react-google-recaptcha";
import logo from "../icons/BellhoptLogoMedRes.png";

const PENDING_HOSPITABLE_OAUTH_KEY = "pending_hospitable_oauth";
const HOSPITABLE_OAUTH_MAX_AGE_MS = 10 * 60 * 1000;
const SAVED_LOGIN_KEY = "saved_login_fields";

const LoginScreen = () => {
  const [initialValues] = useState(() => {
    const saved = sessionStorage.getItem(SAVED_LOGIN_KEY);
    if (saved) {
      sessionStorage.removeItem(SAVED_LOGIN_KEY);
      try { return JSON.parse(saved); } catch { /* ignore */ }
    }
    return { email: "", password: "" };
  });

  const { values, errors, touched, handleBlur, handleChange } = useFormik({
    initialValues: initialValues,
    validationSchema: loginSchema,
  });

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [captchaToken, setCaptchaToken] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const recaptchaRef = useRef(null);

  const onCaptchaChange = (token) => {
    setCaptchaToken(token || "");
  };

  const onCaptchaExpired = () => {
    sessionStorage.setItem(SAVED_LOGIN_KEY, JSON.stringify({ email: values.email, password: values.password }));
    window.location.reload();
  };

  const { loading, hostInfo, error } = useSelector((state) => {
    return state.host;
  });

  const handleLogin = (e) => {
    e.preventDefault();
    if (Object.keys(errors).length === 0 && captchaToken) {
      dispatch(hostLogin({ ...values, captchaResponse: captchaToken }));
    }
  };

  useEffect(() => {
    if (hostInfo) {
      const pendingOauthRaw = sessionStorage.getItem(PENDING_HOSPITABLE_OAUTH_KEY);
      if (pendingOauthRaw) {
        try {
          const pendingOauth = JSON.parse(pendingOauthRaw);
          const createdAt = Number(pendingOauth?.createdAt || 0);
          const isFresh = createdAt > 0 && (Date.now() - createdAt) < HOSPITABLE_OAUTH_MAX_AGE_MS;
          if (pendingOauth?.code && pendingOauth?.state && isFresh) {
            const encodedCode = encodeURIComponent(pendingOauth.code);
            const encodedState = encodeURIComponent(pendingOauth.state);
            navigate(`/integrations/hospitable/callback?code=${encodedCode}&state=${encodedState}`, { replace: true });
            return;
          }
          sessionStorage.removeItem(PENDING_HOSPITABLE_OAUTH_KEY);
        } catch (error) {
          sessionStorage.removeItem(PENDING_HOSPITABLE_OAUTH_KEY);
        }
      }
      const hasOta = hostInfo?.ota === true;
      navigate(hasOta ? "/concierge" : "/host", { replace: true });
    }

    if (error) {
      const t = setTimeout(() => {
        dispatch(hostLoginReset());
      }, 2000);
      return () => clearTimeout(t);
    }
  }, [hostInfo, error, navigate, dispatch]);

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {error && (
        <Alert
          severity="error"
          sx={{
            position: "fixed",
            width: "90%",
            margin: "auto",
            top: 20,
            left: 0,
            right: 0,
            zIndex: 10,
          }}
        >
          <strong>{error}</strong>
          {error !== "Please try again in 15 mins." && (
            <span className="block mt-2">
              Forgot your password?{" "}
              <Link to="/login/forgotPassword" className="font-semibold underline">
                Reset it here
              </Link>
            </span>
          )}
        </Alert>
      )}

      <div className="flex-1 flex items-center justify-center px-4 sm:px-6 lg:px-8 py-12">
        <div className="max-w-md w-full">

          {/* Logo & Hero */}
          <div className="text-center mb-10">
            <div className="inline-block mb-6">
              <img
                src={logo}
                alt="Bellhopt Logo"
                className="h-24 w-auto mx-auto"
              />
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2" style={{ letterSpacing: '-0.5px' }}>
              Welcome Back
            </h1>
            <p className="text-gray-500 text-base">
              Sign in to your Bellhopt dashboard
            </p>
          </div>

          {/* Login Form Card */}
          <div className="bg-gray-100 rounded-2xl p-8 border border-gray-200">
            <div className="text-center mb-6">
              <h2 className="text-xl font-bold text-gray-900 mb-1">Sign In</h2>
              <p className="text-gray-500 text-sm">
                Enter your credentials to access your account
              </p>
            </div>

            <form onSubmit={handleLogin} className="space-y-5">
              {/* Email Field */}
              <div className="space-y-1.5">
                <label className="block text-sm font-semibold text-gray-900" htmlFor="email">
                  Email Address
                  {errors.email && touched.email && (
                    <span className="text-red-500 ml-1">*</span>
                  )}
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207" />
                    </svg>
                  </div>
                  <input
                    name="email"
                    type="email"
                    value={values.email}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    placeholder="Enter your email"
                    className="w-full pl-12 pr-4 py-4 bg-white border border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 transition-all duration-200"
                  />
                </div>
                {errors.email && touched.email && (
                  <p className="text-red-600 text-sm flex items-center">
                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    {errors.email}
                  </p>
                )}
              </div>

              {/* Password Field */}
              <div className="space-y-1.5">
                <label className="block text-sm font-semibold text-gray-900" htmlFor="password">
                  Password
                  {errors.password && touched.password && (
                    <span className="text-red-500 ml-1">*</span>
                  )}
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                  </div>
                  <input
                    name="password"
                    type={showPassword ? "text" : "password"}
                    value={values.password}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    placeholder="Enter your password"
                    className="w-full pl-12 pr-12 py-4 bg-white border border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 transition-all duration-200"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 pr-4 flex items-center text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    {showPassword ? (
                      <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" />
                      </svg>
                    ) : (
                      <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                    )}
                  </button>
                </div>
                {errors.password && touched.password && (
                  <p className="text-red-600 text-sm flex items-center">
                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    {errors.password}
                  </p>
                )}
              </div>

              {/* ReCAPTCHA */}
              <div className="flex justify-center">
                <div className="transform scale-90">
                  <ReCAPTCHA
                    ref={recaptchaRef}
                    sitekey="6LfjWG8pAAAAAPaJUlEtOre0XeDd4K6I43dsBGTq"
                    onChange={onCaptchaChange}
                    onExpired={onCaptchaExpired}
                  />
                </div>
              </div>

              {/* Forgot Password */}
              <div className="text-right">
                <Link
                  to="/login/forgotPassword"
                  className="text-sm font-medium text-gray-500 hover:text-gray-900 transition-colors"
                >
                  Forgot your password?
                </Link>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                onClick={handleLogin}
                disabled={loading || !captchaToken}
                className="w-full flex justify-center items-center py-4 px-6 rounded-xl text-base font-bold text-white bg-gray-900 hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
              >
                {loading ? (
                  <>
                    <CircularProgress
                      color="inherit"
                      className="mr-3"
                      size={24}
                    />
                    Signing In...
                  </>
                ) : (
                  "Sign In to Bellhopt"
                )}
              </button>
            </form>

            {/* Sign Up Link */}
            <div className="mt-6 text-center">
              <p className="text-gray-500 text-sm">
                Don't have an account?{" "}
                <Link
                  to="/signup"
                  className="font-semibold text-gray-900 hover:underline transition-colors"
                >
                  Create your Bellhopt account
                </Link>
              </p>
            </div>
          </div>

          {/* Features Section */}
          <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-gray-100 rounded-2xl p-5 border border-gray-200 text-center">
              <div className="w-10 h-10 bg-gray-200 rounded-xl flex items-center justify-center mx-auto mb-3">
                <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
              </div>
              <h3 className="font-bold text-gray-900 text-sm mb-1">Property Management</h3>
              <p className="text-gray-500 text-xs">Streamline your property operations</p>
            </div>

            <div className="bg-gray-100 rounded-2xl p-5 border border-gray-200 text-center">
              <div className="w-10 h-10 bg-gray-200 rounded-xl flex items-center justify-center mx-auto mb-3">
                <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="font-bold text-gray-900 text-sm mb-1">Track Earnings</h3>
              <p className="text-gray-500 text-xs">Monitor your revenue streams</p>
            </div>

            <div className="bg-gray-100 rounded-2xl p-5 border border-gray-200 text-center">
              <div className="w-10 h-10 bg-gray-200 rounded-xl flex items-center justify-center mx-auto mb-3">
                <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <h3 className="font-bold text-gray-900 text-sm mb-1">Quick Setup</h3>
              <p className="text-gray-500 text-xs">Get started in minutes</p>
            </div>
          </div>

          {/* Footer */}
          <div className="mt-10 text-center">
            <p className="text-sm text-gray-400">
              By signing in, you agree to our{" "}
              <Link to="/privacy" className="text-gray-500 hover:text-gray-900 transition-colors">
                Privacy Policy
              </Link>{" "}
              and{" "}
              <Link to="/terms" className="text-gray-500 hover:text-gray-900 transition-colors">
                Terms of Service
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginScreen;
